import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { getServicePrice } from '../../utils/appointmentUtils'

/**
 * bkBits — shared primitives for the Bookings workspace, ported from the
 * Claude Design "Bookings · desktop" spec (bookings-ui.jsx + the .bk-* CSS in
 * Bookings.html). Used by BookingManager, PendingAppointments and
 * FutureAppointments so the Requests / Upcoming / Past tabs share one visual
 * language.
 */

// Design tokens that aren't in `L` (the shared lavender palette).
export const BK = {
	page: '#FBFAFE',
	card: '#FFFFFF',
	cardShadow: '0 1px 3px rgba(46,42,64,0.04)',
	inset: '#FBFAFE',
	insetBorder: '#F0EDF9',
	rowBorder: '#F5F3FB',
	dayBorder: '#EFEBF7',
	breed: '#9A93B4',
	subtle: '#B4ADC6',
	body: '#6E6886',
	chipText: '#4A4362',
	chipBorder: '#E9E4F6',
	okBg: '#E9F6EE',
	okText: '#25714A',
	warnBg: '#FDF3E4',
	warnBorder: '#F2DDB4',
	warnText: '#8F6217',
	warnIc: '#C08A2D',
	danger: '#BE4B45',
	dangerDeep: '#A53B36',
	dangerSoft: '#FBECEB',
	infoBg: '#E7F1FB',
	infoText: '#2C6CB0',
}

// ── icons (design bk-* set) ─────────────────────────────────────────
export function BkIc({ name, size = 16, sw = 1.6 }) {
	const P = {
		search: <><circle cx="7" cy="7" r="4.6" /><path d="M10.4 10.4L14 14" /></>,
		x: <path d="M4 4l8 8M12 4l-8 8" />,
		check: <path d="M3 8.5l3.5 3.5L13 4.5" />,
		down: <path d="M4 6l4 4 4-4" />,
		calendar: <><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 1.5V4M10.5 1.5V4" /></>,
		clock: <><circle cx="8" cy="8" r="6" /><path d="M8 4.6V8l2.4 1.6" /></>,
		repeat: <><path d="M3 6.5a5 5 0 019.4-1.4" /><path d="M12.6 2.4v2.7h-2.7" /><path d="M13 9.5a5 5 0 01-9.4 1.4" /><path d="M3.4 13.6v-2.7h2.7" /></>,
		ban: <><circle cx="8" cy="8" r="6" /><path d="M3.9 3.9l8.2 8.2" /></>,
		receipt: <><path d="M4 1.8h8v12.4l-2-1.2-2 1.2-2-1.2-2 1.2z" /><path d="M6 5.4h4M6 8h4" /></>,
		user: <><circle cx="8" cy="5.2" r="2.6" /><path d="M3.2 13.8c0-2.6 2.1-4.4 4.8-4.4s4.8 1.8 4.8 4.4" /></>,
		warn: <><path d="M8 2.2L14.5 13H1.5z" /><path d="M8 6.5v3.2" /><circle cx="8" cy="11.4" r=".8" fill="currentColor" stroke="none" /></>,
		msg: <path d="M2.5 3.5h11v8h-6L4 14v-2.5H2.5v-8z" />,
		refresh: <><path d="M13.5 8a5.5 5.5 0 11-1.6-3.9" /><path d="M13.8 1.8v2.7h-2.7" /></>,
		paw: <><circle cx="5.2" cy="4.6" r="1.3" /><circle cx="10.8" cy="4.6" r="1.3" /><circle cx="2.9" cy="8.1" r="1.2" /><circle cx="13.1" cy="8.1" r="1.2" /><path d="M8 7.6c1.8 0 3.4 1.5 3.4 3.2 0 1.2-.85 2.1-2 2.1-.55 0-.95-.2-1.4-.2s-.85.2-1.4.2c-1.15 0-2-.9-2-2.1 0-1.7 1.6-3.2 3.4-3.2z" /></>,
		dots: <><circle cx="8" cy="3.2" r="1.35" fill="currentColor" stroke="none" /><circle cx="8" cy="8" r="1.35" fill="currentColor" stroke="none" /><circle cx="8" cy="12.8" r="1.35" fill="currentColor" stroke="none" /></>,
	}
	return (
		<svg
			width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
			strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
			style={{ flex: 'none', display: 'block' }}
		>
			{P[name] || null}
		</svg>
	)
}

// ── .bk-avatar ──────────────────────────────────────────────────────
/**
 * Circular pet avatar. Shows the pet's photo when `src` is given (see
 * usePetAvatars), otherwise the paw placeholder — which is also the fallback
 * if the image itself fails to load.
 */
export function BkAvatar({ size = 38, src = null, alt = '' }) {
	const [failed, setFailed] = useState(false)
	const showImage = !!src && !failed

	// A new src (avatar re-uploaded, row recycled) gets a fresh chance to load.
	useEffect(() => { setFailed(false) }, [src])

	return (
		<Box component="span" sx={{
			width: size, height: size, flex: 'none', borderRadius: '50%',
			bgcolor: L.primarySoft, color: L.primaryDeep, overflow: 'hidden',
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
		}}>
			{showImage ? (
				<Box
					component="img"
					src={src}
					alt={alt}
					loading="lazy"
					onError={() => setFailed(true)}
					sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
				/>
			) : (
				<BkIc name="paw" size={Math.round(size * 0.46)} sw={1.4} />
			)}
		</Box>
	)
}

// ── .bk-pets — per-pet blocks inside a request ──────────────────────
/**
 * One block per pet (design `.bk-pets` / `.bk-pet`), so a two-pet booking
 * never mixes up whose breed or add-on is whose: avatar + pet name/breed +
 * price on the first row, the service + minutes underneath, then every
 * add-on on its own line. A single pet is just a stack of one.
 *
 * pets: [{ key, name, breed, service, mins, price, addons: [name], avatarUrl }]
 */
export function BkPetLines({ pets }) {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
			{pets.map((p, i) => (
				<Box key={p.key ?? i} sx={{
					display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto',
					columnGap: '10px', rowGap: '5px', alignItems: 'center', minWidth: 0,
					...(i > 0 ? { borderTop: '1px solid #F1EDFA', pt: '12px' } : {}),
				}}>
					<Box sx={{ gridRow: 1, gridColumn: 1, display: 'flex' }}>
						<BkAvatar size={30} src={p.avatarUrl} alt={p.name || ''} />
					</Box>
					<Box sx={{
						gridRow: 1, gridColumn: 2, display: 'flex', alignItems: 'baseline',
						gap: '7px', minWidth: 0, flexWrap: 'wrap',
					}}>
						<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, letterSpacing: '-0.005em' }}>
							{p.name}
						</Box>
						{p.breed ? <Box component="span" sx={bkBreedSx}>{p.breed}</Box> : null}
					</Box>
					<Box sx={{ gridRow: 1, gridColumn: 3, fontSize: '13px', fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>
						{bkMoney(p.price)}
					</Box>
					<Box sx={{ gridRow: 2, gridColumn: '2 / -1', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
							<Box component="span" sx={bkSvcNameSx}>{p.service}</Box>
							{p.mins ? (
								<Box component="span" sx={{
									flex: 'none', fontSize: '12px', fontWeight: 650, color: BK.breed,
									whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
								}}>
									{p.mins} min
								</Box>
							) : null}
						</Box>
						{p.addons?.length ? (
							<Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
								{p.addons.map((a, j) => (
									<Box component="li" key={j} sx={{
										display: 'flex', alignItems: 'flex-start', gap: '6px', minWidth: 0,
										fontSize: '12.5px', fontWeight: 600, color: BK.body, lineHeight: 1.35,
									}}>
										<Box component="span" aria-hidden="true" sx={{ flex: 'none', color: BK.subtle, fontWeight: 700, lineHeight: 1.35 }}>+</Box>
										<span>{a}</span>
									</Box>
								))}
							</Box>
						) : null}
					</Box>
				</Box>
			))}
		</Box>
	)
}

// ── .bk-popwrap + .bk-menu — triple-dot overflow menu ───────────────
/**
 * Icon-button overflow menu (design `.bk-menu`). `items` is a list of
 * { icon, label, danger, onClick } — pass the string 'sep' for a divider.
 */
export function BkRowMenu({ items, disabled = false, title = 'More actions' }) {
	const [open, setOpen] = useState(false)
	return (
		<Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
			<Box
				component="button" type="button" disabled={disabled} title={title} aria-expanded={open}
				onClick={() => setOpen((o) => !o)}
				sx={{
					...bkIconBtnSx(false),
					...(open ? { color: L.primaryDeep, borderColor: L.primary, bgcolor: L.primarySoft } : {}),
				}}
			>
				<BkIc name="dots" size={14} />
			</Box>
			{open ? (
				<>
					<Box onClick={() => setOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 40 }} />
					<Box
						role="menu"
						onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
						sx={{
							position: 'absolute', top: 36, right: 0, zIndex: 45, minWidth: 216, p: '6px',
							bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '13px',
							boxShadow: '0 18px 44px rgba(95,78,194,0.18), 0 3px 10px rgba(95,78,194,0.08)',
						}}
					>
						{items.map((it, i) => it === 'sep' ? (
							<Box key={i} sx={{ display: 'block', height: '1px', m: '5px 4px', bgcolor: '#F1EDF9' }} />
						) : (
							<Box
								component="button" type="button" role="menuitem" key={i}
								onClick={() => { setOpen(false); it.onClick() }}
								sx={{
									display: 'flex', alignItems: 'center', gap: '10px', width: '100%', p: '9px 10px',
									border: 'none', bgcolor: 'transparent', borderRadius: '9px', fontFamily: 'inherit',
									fontSize: '13px', fontWeight: 650, textAlign: 'left', whiteSpace: 'nowrap', cursor: 'pointer',
									color: it.danger ? BK.danger : L.text,
									'&:hover': it.danger
										? { bgcolor: BK.dangerSoft, color: BK.danger }
										: { bgcolor: L.primarySoft, color: L.primaryDeep },
									'&:hover .bk-menu-ic': { color: it.danger ? BK.danger : L.primaryDeep },
								}}
							>
								<Box className="bk-menu-ic" sx={{ display: 'flex', flex: 'none', color: it.danger ? BK.danger : L.muted }}>
									<BkIc name={it.icon} size={14} />
								</Box>
								<span>{it.label}</span>
							</Box>
						))}
					</Box>
				</>
			) : null}
		</Box>
	)
}

// ── .bk-flag ────────────────────────────────────────────────────────
const FLAG_TONE = {
	first: { bg: BK.infoBg, fg: BK.infoText, ic: 'user' },
	multi: { bg: L.primarySoft, fg: L.primaryDeep, ic: 'paw' },
	repeat: { bg: L.primarySoft, fg: L.primaryDeep, ic: 'repeat' },
	warn: { bg: BK.warnBg, fg: BK.warnText, ic: 'clock' },
}
export function BkFlag({ type = 'multi', label }) {
	const t = FLAG_TONE[type] || FLAG_TONE.multi
	return (
		<Box component="span" title={label} sx={{
			display: 'inline-flex', alignItems: 'center', gap: '4px', height: 20, px: '8px',
			borderRadius: '99px', fontSize: '11px', fontWeight: 750, whiteSpace: 'nowrap',
			bgcolor: t.bg, color: t.fg, flex: 'none',
		}}>
			<BkIc name={t.ic} size={11} sw={1.5} />
			<span>{label}</span>
		</Box>
	)
}

// ── .bk-pill (status) ───────────────────────────────────────────────
const PILL_TONE = {
	ok: { bg: BK.okBg, fg: BK.okText },
	warn: { bg: BK.warnBg, fg: BK.warnText },
	muted: { bg: L.surface, fg: L.muted },
	info: { bg: BK.infoBg, fg: BK.infoText },
}
export function BkPill({ tone = 'muted', label }) {
	const t = PILL_TONE[tone] || PILL_TONE.muted
	return (
		<Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: '6px', height: 22, px: '9px',
			borderRadius: '99px', fontSize: '11.5px', fontWeight: 750, whiteSpace: 'nowrap',
			bgcolor: t.bg, color: t.fg,
		}}>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flex: 'none' }} />
			<span>{label}</span>
		</Box>
	)
}

// ── .bk-gr (groomer chip) ───────────────────────────────────────────
export const bkInitials = (name) => (name || '')
	.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()

export function BkGroomer({ name, mutedLabel = 'Any groomer' }) {
	if (!name) {
		return (
			<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: BK.subtle, whiteSpace: 'nowrap' }}>
				{mutedLabel}
			</Box>
		)
	}
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
			<Box component="span" sx={{
				width: 22, height: 22, flex: 'none', borderRadius: '50%', bgcolor: L.surface,
				border: `1px solid ${L.border}`, color: BK.body, fontSize: '9.5px', fontWeight: 800,
				display: 'inline-flex', alignItems: 'center', justifyContent: 'center', letterSpacing: '0.02em',
			}}>
				{bkInitials(name)}
			</Box>
			<Box component="span" sx={{
				fontSize: '12.5px', fontWeight: 650, color: BK.body,
				whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
			}}>
				{name}
			</Box>
		</Box>
	)
}

// ── .bk-empty ───────────────────────────────────────────────────────
export function BkEmpty({ icon = 'check', title, sub }) {
	return (
		<Box sx={{
			p: '44px 16px', textAlign: 'center', bgcolor: BK.card,
			border: `1px dashed ${L.border}`, borderRadius: '14px',
			display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
		}}>
			<Box sx={{
				width: 42, height: 42, borderRadius: '50%', bgcolor: L.primarySoft, color: L.primaryDeep,
				display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '4px',
			}}>
				<BkIc name={icon} size={20} sw={1.5} />
			</Box>
			<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 750 }}>{title}</Box>
			{sub ? <Box component="span" sx={{ fontSize: '13px', fontWeight: 600, color: L.muted }}>{sub}</Box> : null}
		</Box>
	)
}

// ── text bits (.bk-petline / .bk-breed / .bk-subline / .bk-svcname) ──
export const bkPetLineSx = {
	display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0, flexWrap: 'wrap',
	'& > strong': { fontSize: '14px', fontWeight: 750, letterSpacing: '-0.005em' },
}
export const bkBreedSx = { fontSize: '12px', fontWeight: 600, color: BK.breed, whiteSpace: 'nowrap' }
export const bkSublineSx = {
	fontSize: '12px', fontWeight: 600, color: L.muted,
	whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
export const bkIdColSx = { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }
export const bkSvcNameSx = { fontSize: '13px', fontWeight: 700, letterSpacing: '-0.005em' }

// ── buttons ─────────────────────────────────────────────────────────
const btnBase = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
	fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none',
	'&:disabled': { cursor: 'default' },
}

// .bk-approve
export const bkApproveSx = (sm = false) => ({
	...btnBase,
	gap: '7px', height: sm ? 32 : 36, px: sm ? '12px' : '15px', border: 'none', borderRadius: '9px',
	bgcolor: L.primary, color: L.onPrimary, fontSize: sm ? '12.5px' : '13px', fontWeight: 700,
	boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
	'&:hover': { bgcolor: L.primaryDeep },
	'&:disabled': { bgcolor: L.primarySoft, color: '#A99DDD', boxShadow: 'none' },
})

// .bk-ghost
export const bkGhostSx = (sm = false) => ({
	...btnBase,
	gap: '7px', height: sm ? 34 : 40, px: sm ? '12px' : '14px',
	border: `1.5px solid ${L.border}`, borderRadius: sm ? '9px' : '10px',
	bgcolor: '#FFFFFF', color: L.text, fontSize: sm ? '12.5px' : '13px', fontWeight: 650,
	'&:hover': { borderColor: L.primary, color: L.primaryDeep },
	'&:disabled': { color: '#B4ADC6', borderColor: L.border },
})

// .bk-iconbtn
export const bkIconBtnSx = (danger = false) => ({
	...btnBase,
	width: 30, height: 30, flex: 'none', border: `1px solid ${L.border}`, borderRadius: '8px',
	bgcolor: '#FFFFFF', color: L.muted,
	'&:hover': danger
		? { color: BK.danger, borderColor: BK.danger, bgcolor: BK.dangerSoft }
		: { color: L.primaryDeep, borderColor: L.primary },
	'&:disabled': { color: '#CFC9DE', borderColor: L.border, bgcolor: '#FFFFFF' },
})

// .bk-declbtn
export const bkDeclineSx = {
	...btnBase,
	height: 34, px: '10px', border: 'none', borderRadius: '8px', bgcolor: 'transparent',
	color: BK.danger, fontSize: '12.5px', fontWeight: 650,
	'&:hover': { bgcolor: BK.dangerSoft },
	'&:disabled': { color: '#D89893' },
}

// .bk-dangerbtn — destructive confirm ("Cancel & notify")
export const bkDangerBtnSx = {
	...btnBase,
	gap: '7px', height: 36, px: '14px', border: 'none', borderRadius: '9px',
	bgcolor: BK.danger, color: '#FFFFFF', fontSize: '12.5px', fontWeight: 700,
	'&:hover': { bgcolor: BK.dangerDeep },
	'&:disabled': { bgcolor: BK.dangerSoft, color: '#D89893' },
}

// .bk-fchip
export const bkFilterChipSx = (on = false) => ({
	...btnBase,
	gap: '7px', height: 34, px: '13px', borderRadius: '99px',
	border: `1.5px solid ${on ? L.primary : L.border}`,
	bgcolor: on ? L.primarySoft : '#FFFFFF',
	color: on ? L.primaryDeep : L.muted,
	fontSize: '12.5px', fontWeight: 700,
	'&:hover': { color: on ? L.primaryDeep : L.text, borderColor: L.primary },
})

// .bk-reqtime — the requested-time pill on a request row
export const bkTimeChipSx = (warn = false) => ({
	display: 'inline-flex', alignItems: 'center', gap: '7px', height: 32, px: '13px',
	borderRadius: '99px', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
	bgcolor: warn ? BK.warnBg : BK.inset,
	border: `1px solid ${warn ? BK.warnBorder : BK.chipBorder}`,
	color: warn ? BK.warnText : BK.chipText,
})

// .bk-day card + .bk-dayhead
export const bkDayCardSx = {
	bgcolor: BK.card, border: `1px solid ${L.border}`, borderRadius: '14px',
	mb: '14px', boxShadow: BK.cardShadow,
}
export const bkDayHeadSx = {
	display: 'flex', alignItems: 'baseline', gap: '10px', p: '13px 16px 11px',
	borderBottom: `1px solid ${BK.dayBorder}`, flexWrap: 'wrap',
}

// ── formatting ──────────────────────────────────────────────────────
export const bkMoney = (cents) => {
	const n = (cents || 0) / 100
	return '$' + (Number.isInteger(n) ? n : n.toFixed(2))
}

/** Total price of one appointment row: service price + every selected add-on. */
export const bkApptPrice = (a) => {
	const svc = getServicePrice(a)
	const adds = Array.isArray(a?.add_ons)
		? a.add_ons.reduce((s, x) => s + (x?.price || x?.amount || 0), 0)
		: 0
	return svc + adds
}

export const bkFullName = (e) => e ? [e.first_name, e.last_name].filter(Boolean).join(' ') : ''

/** Case-insensitive substring match of `q` against any of the given fields. */
export const bkHit = (q, ...fields) => {
	const needle = (q || '').trim().toLowerCase()
	if (!needle) return true
	return fields.some((f) => String(f || '').toLowerCase().includes(needle))
}

// .bk-search — header search box (a focus-within-styled label wrapping an input)
export const bkSearchSx = {
	display: 'flex', alignItems: 'center', gap: '8px', height: 40, width: 230,
	px: '13px', borderRadius: '10px', bgcolor: '#FFFFFF',
	border: `1.5px solid ${L.border}`, color: L.muted,
	'&:focus-within': { borderColor: L.primary },
	'& input': {
		border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit',
		fontSize: '13.5px', color: L.text, width: '100%', p: 0,
		'&::placeholder': { color: L.muted, opacity: 1 },
	},
}
